<script setup>
import { ref, computed, onMounted, onBeforeUnmount, nextTick, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { vOnClickOutside } from '@vueuse/components';
import { LocalStorage } from 'shared/helpers/localStorage';
import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';
import { setColorTheme } from 'dashboard/helper/themeHelper.js';
import { useAlert } from 'dashboard/composables';
import BotsAPI from 'dashboard/api/bots';
import InboxesAPI from 'dashboard/api/inboxes';
import LabelsAPI from 'dashboard/api/labels';
import AgentsAPI from 'dashboard/api/agents';
import PipelinesAPI from 'dashboard/api/pipelines';
import StepNode from './components/StepNode.vue';
import StepConfigPanel from './components/StepConfigPanel.vue';
import TriggerEditor from './components/TriggerEditor.vue';
import StepPalettePopover from './components/StepPalettePopover.vue';

const route = useRoute();
const router = useRouter();

const bot = ref(null);
const triggers = ref([]);
const steps = ref([]);
const loading = ref(true);
const saving = ref(false);
const availableBots = ref([]);
const availableInboxes = ref([]);
const availableLabels = ref([]);
const availableAgents = ref([]);
const availablePipelines = ref([]);

// UI state
const selectedStep = ref(null);
const selectedTrigger = ref(null);
const showTriggerEditor = ref(false);
const palettePos = ref(null);

// Drag (de un nodo)
const dragging = ref(null);

// Pan/Zoom del canvas (Fase 7)
const zoom = ref(1);
const pan = ref({ x: 0, y: 0 });
const panning = ref(null);
const SNAP = 24; // grid snap

const STEP_W = 240;
const CANVAS_W = 50000;
const CANVAS_H = 50000;

// Límites del área de trabajo · evitan que el usuario "se pierda" en el infinito.
// Margen de "rejuego" hacia la izquierda/arriba del entry pill, y límite máximo a la derecha/abajo.
const PAN_LEFT_MARGIN = 200;     // máximo de espacio vacío visible a la izquierda del entry pill
const PAN_TOP_MARGIN = 100;      // máximo de espacio vacío visible arriba del entry pill
const WORK_AREA_MIN_WIDTH = 8000;  // base mínima de área de trabajo a la derecha
const WORK_AREA_MIN_HEIGHT = 5000; // base mínima hacia abajo
const WORK_AREA_BUFFER = 3000;     // 2026-05-14 (Victor) · espacio extra siempre disponible después del último step para seguir agregando

// V2 · 2026-05-14 (Victor) · área de trabajo dinámica que crece con los steps.
// Antes era fijo 8000x5000 · si el bot crecía horizontalmente más allá de eso
// el pan se bloqueaba y no se podía ver más a la derecha. Ahora calcula el
// bounding box de los steps + buffer de 3000px para tener siempre margen.
function workAreaWidth() {
  if (!steps.value || steps.value.length === 0) return WORK_AREA_MIN_WIDTH;
  const rightEdges = steps.value.map(s => (s.canvas_x || 0) + stepWidth(s));
  const maxX = Math.max(WORK_AREA_MIN_WIDTH, ...rightEdges);
  return maxX + WORK_AREA_BUFFER;
}
function workAreaHeight() {
  if (!steps.value || steps.value.length === 0) return WORK_AREA_MIN_HEIGHT;
  const bottomEdges = steps.value.map(s => (s.canvas_y || 0) + 400); // step heuristic height
  const maxY = Math.max(WORK_AREA_MIN_HEIGHT, ...bottomEdges);
  return maxY + WORK_AREA_BUFFER;
}

function clampPan() {
  const vp = viewportRef.value;
  if (!vp) return;
  const z = zoom.value;
  const wW = workAreaWidth();
  const wH = workAreaHeight();
  // pan.x positivo = canvas desplazado a la derecha en pantalla (más espacio a la izquierda).
  // Limitamos pan.x ≤ PAN_LEFT_MARGIN para que no haya más de 200px vacíos a la izq.
  // Y pan.x ≥ vp.width − workAreaWidth * zoom para que el área útil no se vaya off-screen a la der.
  pan.value = {
    x: Math.min(PAN_LEFT_MARGIN, Math.max(vp.clientWidth - wW * z, pan.value.x)),
    y: Math.min(PAN_TOP_MARGIN, Math.max(vp.clientHeight - wH * z, pan.value.y)),
  };
}

// Ancho real renderizado por tipo de step (para anclar líneas al borde correcto del card)
function stepWidth(step) {
  if (step?.step_type === 'message') return 420;
  if (step?.step_type === 'condition') return 320;
  if (step?.step_type === 'pause') return 320;
  if (step?.step_type === 'action' && step?.config?.action_type === 'add_label') return 340;
  // V5-P2 · Cambiar etapa (2 secciones Ejecutar + Acción) layout horizontal wide
  if (step?.step_type === 'action' && step?.config?.action_type === 'change_stage') return 880;
  return 288;
}

// ── Cálculo de la Y donde nace una rama de un step de condición ──
// Cada valor tiene su propia fila visible en el card, así que la línea debe nacer
// de la Y de esa fila específica (no del top-right del card).
const COND_LAYOUT = {
  HEADER: 28,        // header del card (#id, icono, nombre, ⋯, ×)
  BODY_PAD: 8,       // padding del body interior
  RULE_PAD: 10,      // padding del rule block (border dashed)
  PILLS_H: 32,       // fila de pills (Si + 3 pills) + mb-2
  VALUE_ROW_H: 30,   // alto de cada fila de valor (incluye gap)
  ADD_VALUE_H: 22,   // botón "+ Agregar valor"
  AND_BLOCK_H: 48,   // bloque AND condition (estimado)
  AND_FOOTER_H: 32,  // border-t + "+ Agregar condición (Y)"
  EXTRA_GAP: 8,      // mt-2 entre rule blocks
  ADD_RULE_H: 22,    // botón "+ Agregar nueva condición"
  ELSE_H: 36,        // fila del else
};

function ruleBlockHeight(rule) {
  const values = Array.isArray(rule?.values) ? rule.values : (rule?.value !== undefined ? [rule.value] : ['']);
  const ands = Array.isArray(rule?.and_conditions) ? rule.and_conditions : [];
  return COND_LAYOUT.RULE_PAD * 2
    + COND_LAYOUT.PILLS_H
    + values.length * COND_LAYOUT.VALUE_ROW_H
    + COND_LAYOUT.ADD_VALUE_H
    + ands.length * COND_LAYOUT.AND_BLOCK_H
    + COND_LAYOUT.AND_FOOTER_H;
}

// Ancla Y para la línea que sale del step `parent` con branch_key dado.
function condBranchOriginY(parent, branchKey) {
  // Steps no-condition o branches legacy → top-right (comportamiento actual)
  if (!parent || parent.step_type !== 'condition') return parent ? parent.canvas_y + 14 : 0;
  if (branchKey === 'true' || branchKey === 'false') return parent.canvas_y + 14;

  const cfg = parent.config || {};
  const baseY = parent.canvas_y + COND_LAYOUT.HEADER + COND_LAYOUT.BODY_PAD;

  // value:N → fila N de la primary rule
  if (typeof branchKey === 'string' && branchKey.startsWith('value:')) {
    const idx = parseInt(branchKey.split(':')[1], 10);
    return baseY
      + COND_LAYOUT.RULE_PAD
      + COND_LAYOUT.PILLS_H
      + idx * COND_LAYOUT.VALUE_ROW_H
      + COND_LAYOUT.VALUE_ROW_H / 2;
  }

  // rule:N:value:M → fila M de la regla extra N (1-indexed: rule:1 = primer extra)
  if (typeof branchKey === 'string' && branchKey.startsWith('rule:')) {
    const m = branchKey.match(/^rule:(\d+):value:(\d+)$/);
    if (m) {
      const ruleIdx = parseInt(m[1], 10);
      const valIdx = parseInt(m[2], 10);
      let y = baseY;
      // primary
      const primary = {
        values: Array.isArray(cfg.values) ? cfg.values : [cfg.value || ''],
        and_conditions: cfg.and_conditions || [],
      };
      y += ruleBlockHeight(primary);
      // extras anteriores
      const extras = Array.isArray(cfg.extra_rules) ? cfg.extra_rules : [];
      for (let i = 0; i < ruleIdx - 1; i++) {
        y += COND_LAYOUT.EXTRA_GAP;
        y += ruleBlockHeight(extras[i] || {});
      }
      // regla actual: gap + padding + pills + valIdx filas + centro
      y += COND_LAYOUT.EXTRA_GAP;
      y += COND_LAYOUT.RULE_PAD;
      y += COND_LAYOUT.PILLS_H;
      y += valIdx * COND_LAYOUT.VALUE_ROW_H;
      y += COND_LAYOUT.VALUE_ROW_H / 2;
      return y;
    }
  }

  // else → fila amber al final del card
  if (branchKey === 'else') {
    let y = baseY;
    const primary = {
      values: Array.isArray(cfg.values) ? cfg.values : [cfg.value || ''],
      and_conditions: cfg.and_conditions || [],
    };
    y += ruleBlockHeight(primary);
    const extras = Array.isArray(cfg.extra_rules) ? cfg.extra_rules : [];
    extras.forEach(r => {
      y += COND_LAYOUT.EXTRA_GAP + ruleBlockHeight(r);
    });
    y += COND_LAYOUT.EXTRA_GAP + COND_LAYOUT.ADD_RULE_H;
    y += COND_LAYOUT.ELSE_H / 2;
    return y;
  }

  // Default: top-right
  return parent.canvas_y + 14;
}

// Posiciones de tarjetas decorativas (arrastrables, persistidas en localStorage)
const entryNodePos = ref({ x: 360, y: 240 });
const triggerCardPos = ref({ x: 60, y: 200 });
const draggingDeco = ref(null);
// Steps que el usuario desconectó explícitamente (doble click en su línea).
// No se dibuja línea para ellos — quedan flotantes hasta que se reconecten.
const disconnectedStepIds = ref(new Set());

// ── Modo conexión: usuario arrastra una línea desde un step a otro ──
const connectingFrom = ref(null);          // step origen (objeto)
const connectingBranchKey = ref(null);     // branch_key heredado del botón origen (value:N, rule:N:value:M, else, o null)
const connectingMousePos = ref({ x: 0, y: 0 }); // posición cursor en coords del canvas
const hoveredTargetId = ref(null);         // id del step bajo el cursor
const connectionFlash = ref(null);         // { stepId } para animación al confirmar

const stepsById = computed(() => Object.fromEntries(steps.value.map(s => [s.id, s])));
// Los pasos "Ir a" no se dibujan como nodo-cajón: se ven como flecha directa
// (convergencia multi-entrada estilo Kommo). Existen en datos para el runner.
const nodeSteps = computed(() => steps.value.filter(s => s.step_type !== 'goto'));
const botId = computed(() => Number(route.params.botId));

async function load() {
  loading.value = true;
  try {
    const [b, t, s, all, inb, lab, agt, pip] = await Promise.all([
      BotsAPI.show(botId.value),
      BotsAPI.triggers(botId.value),
      BotsAPI.steps(botId.value),
      BotsAPI.get(),
      InboxesAPI.get(),
      LabelsAPI.get().catch(() => ({ data: { payload: [] } })),
      AgentsAPI.get().catch(() => ({ data: [] })),
      PipelinesAPI.get().catch(() => ({ data: { payload: [] } })),
    ]);
    bot.value = b.data;
    triggers.value = t.data.payload || [];
    steps.value = s.data.payload || [];
    availableBots.value = (all.data.payload || []).filter(x => x.id !== botId.value);
    availableInboxes.value = inb.data.payload || [];
    availableLabels.value = lab.data.payload || lab.data || [];
    availableAgents.value = agt.data.payload || agt.data || [];
    availablePipelines.value = pip.data.payload || [];
    autoLayoutIfNeeded();
    loadLayout();
    nextTick(fitToScreen);
  } finally {
    loading.value = false;
  }
}

function autoLayoutIfNeeded() {
  const allZero = steps.value.every(s => s.canvas_x === 0 && s.canvas_y === 0);
  if (!allZero || steps.value.length === 0) return;
  applyAutoLayout();
}

function applyAutoLayout() {
  const childrenOf = {};
  steps.value.forEach(s => {
    const pid = s.parent_step_id || 'root';
    childrenOf[pid] = childrenOf[pid] || [];
    childrenOf[pid].push(s);
  });
  let yCursor = 200;
  function place(nodeList, depth) {
    nodeList.forEach((s) => {
      s.canvas_x = 660 + depth * 280;
      s.canvas_y = yCursor;
      yCursor += 160;
      const ch = childrenOf[s.id] || [];
      place(ch, depth + 1);
    });
  }
  place(childrenOf['root'] || [], 0);
  steps.value.forEach(s => saveStepPosition(s));
}

// True si existe un paso raíz que sale de "Iniciar Salesbot" (sin parent y no marcado como desconectado).
const hasRootStep = computed(() =>
  steps.value.some(s => !s.parent_step_id && !disconnectedStepIds.value.has(s.id))
);

// ── Connectors con routing ortogonal ────────────────────────
const connectors = computed(() => {
  const lines = [];
  steps.value.forEach(s => {
    // Si el usuario lo desconectó explícitamente → no dibujar línea
    if (disconnectedStepIds.value.has(s.id)) return;
    let from;
    let parent = null;
    if (s.parent_step_id) {
      parent = stepsById.value[s.parent_step_id];
      if (!parent) return;
      // Origen: para condition con value:N / rule:N:value:M / else, sale de la fila del valor.
      // Para legacy (true/false) o no-condition, top-right del card.
      from = { x: parent.canvas_x + stepWidth(parent), y: condBranchOriginY(parent, s.branch_key) };
    } else {
      from = { x: entryNodePos.value.x + 176, y: entryNodePos.value.y + 16 };
    }
    // Paso "Ir a": invisible como nodo · se dibuja como FLECHA DIRECTA del
    // origen al destino real (convergencia multi-entrada estilo Kommo).
    if (s.step_type === 'goto') {
      const targetId = (s.config || {}).target_step_id;
      const tgt = targetId && stepsById.value[targetId];
      if (!tgt) return;
      lines.push({
        id: s.id,
        from,
        to: { x: tgt.canvas_x, y: tgt.canvas_y + 14 },
        branch: s.branch_key,
        label: null,
        labelColor: null,
        isGoto: true,
      });
      return;
    }
    // Destino: top-left del card (junto al # de id)
    const to = { x: s.canvas_x, y: s.canvas_y + 14 };
    // Resolver label de la rama (Fase 2A: value:N → valor real, else → Ninguna; legacy: true/false)
    let label = null;
    let labelColor = null;
    const bk = s.branch_key;
    if (bk === 'true') { label = 'Sí'; labelColor = '#059669'; }
    else if (bk === 'false') { label = 'No'; labelColor = '#dc2626'; }
    else if (bk === 'else') { label = 'Ninguna'; labelColor = '#b45309'; }
    else if (bk && bk.startsWith('value:') && parent?.config) {
      const idx = parseInt(bk.split(':')[1], 10);
      const values = Array.isArray(parent.config.values) ? parent.config.values : (parent.config.value !== undefined ? [parent.config.value] : []);
      const v = values[idx];
      label = v !== undefined && v !== '' ? `"${String(v)}"` : `valor ${idx + 1}`;
      labelColor = '#475569';
    }
    else if (bk && bk.startsWith('rule:') && parent?.config) {
      // rule:N:value:M → resolver desde extra_rules[N-1].values[M]
      const m = bk.match(/^rule:(\d+):value:(\d+)$/);
      if (m) {
        const ruleIdx = parseInt(m[1], 10);
        const valIdx = parseInt(m[2], 10);
        const extras = Array.isArray(parent.config.extra_rules) ? parent.config.extra_rules : [];
        const rule = extras[ruleIdx - 1];
        const values = rule?.values || [];
        const v = values[valIdx];
        label = v !== undefined && v !== '' ? `R${ruleIdx + 1}: "${String(v)}"` : `R${ruleIdx + 1} valor ${valIdx + 1}`;
        labelColor = '#7c3aed';
      }
    }
    lines.push({ id: s.id, from, to, branch: bk, label, labelColor });
  });
  return lines;
});

function pathFor(line) {
  // bezier curve
  const dx = Math.max(40, (line.to.x - line.from.x) * 0.5);
  return `M${line.from.x},${line.from.y} C${line.from.x + dx},${line.from.y} ${line.to.x - dx},${line.to.y} ${line.to.x},${line.to.y}`;
}

// ── Step CRUD ───────────────────────────────────────────────
function openPaletteFromNode(parentStep, ev, branchKey = 'next') {
  // Usamos el botón mismo (currentTarget) — siempre devuelve la posición correcta
  const rect = ev?.currentTarget?.getBoundingClientRect?.();
  if (rect) {
    palettePos.value = {
      x: rect.left,
      y: rect.bottom + 4,
      parentStep,
      branchKey,
    };
  } else {
    // fallback al centro del viewport si no se pasó el evento
    const vp = viewportRef.value;
    palettePos.value = {
      x: (vp?.clientWidth || 600) / 2,
      y: (vp?.clientHeight || 400) / 2,
      parentStep,
      branchKey,
    };
  }
}

function openPaletteFromEntry() {
  palettePos.value = {
    x: entryNodePos.value.x + 200,
    y: entryNodePos.value.y + 200,
    parentStep: null,
    branchKey: null,
  };
}

async function pickStepType(stepType) {
  if (!palettePos.value) return;
  const parent = palettePos.value.parentStep;
  const branchKey = palettePos.value.branchKey;
  // Atajo: 'add_label' es un alias del usuario que crea un step `action` con
  // action_type=add_label preconfigurado (UX directa sin pasar por dropdown).
  const isLabelShortcut = stepType === 'add_label';
  // V6 · 2026-05-14 (Victor) · Submenú de Acciones: stepType puede venir como
  // 'action__<subtype>' (ej. 'action__change_stage'). Parseamos el subtype para
  // setear el action_type correcto en el config inicial.
  let actionSubtype = null;
  if (typeof stepType === 'string' && stepType.startsWith('action__')) {
    actionSubtype = stepType.slice('action__'.length);
    stepType = 'action';
  }
  const realType = isLabelShortcut ? 'action' : stepType;
  // Posicionar nuevo step a la derecha del padre con gap fijo (compensa anchos distintos por tipo)
  const baseX = parent ? parent.canvas_x + stepWidth(parent) + 80 : 660;
  // Offset Y según el tipo de rama
  let yOffset = 0;
  if (branchKey === 'false') yOffset = 140;
  else if (branchKey === 'else') {
    // "Ninguna coincide" va abajo de todas las ramas (primary values + extra rules values)
    const primaryLen = Array.isArray(parent?.config?.values) ? parent.config.values.length : 1;
    const extras = Array.isArray(parent?.config?.extra_rules) ? parent.config.extra_rules : [];
    const extrasLen = extras.reduce((sum, r) => sum + (Array.isArray(r.values) ? r.values.length : 1), 0);
    yOffset = 120 * (primaryLen + extrasLen);
  } else if (typeof branchKey === 'string' && branchKey.startsWith('value:')) {
    const idx = parseInt(branchKey.split(':')[1], 10);
    yOffset = 120 * idx;
  } else if (typeof branchKey === 'string' && branchKey.startsWith('rule:')) {
    // rule:N:value:M → posicionar después de las ramas primary y de las reglas extra anteriores
    const m = branchKey.match(/^rule:(\d+):value:(\d+)$/);
    if (m) {
      const ruleIdx = parseInt(m[1], 10);
      const valIdx = parseInt(m[2], 10);
      const primaryLen = Array.isArray(parent?.config?.values) ? parent.config.values.length : 1;
      const extras = Array.isArray(parent?.config?.extra_rules) ? parent.config.extra_rules : [];
      let prevExtrasLen = 0;
      for (let i = 0; i < ruleIdx - 1; i++) {
        prevExtrasLen += Array.isArray(extras[i]?.values) ? extras[i].values.length : 1;
      }
      yOffset = 120 * (primaryLen + prevExtrasLen + valIdx);
    }
  }
  const baseY = parent ? parent.canvas_y + yOffset : 240;
  // V6 · Si vino un subtype del submenú "Acciones", lo aplicamos al action_type
  // inicial. Sino fallback a defaultConfigFor (que pone add_label por compat).
  const defaultConfig = isLabelShortcut
    ? { action_type: 'add_label', labels: [] }
    : (actionSubtype
        ? defaultActionConfigFor(actionSubtype)
        : defaultConfigFor(realType));
  try {
    const { data } = await BotsAPI.createStep(botId.value, {
      step_type: realType,
      parent_step_id: parent?.id || null,
      branch_key: branchKey,
      canvas_x: snap(baseX),
      canvas_y: snap(baseY),
      config: defaultConfig,
    });
    steps.value.push(data);
    selectedStep.value = data;
  } finally {
    palettePos.value = null;
  }
}

// V6 · Configs iniciales por subtipo de acción (Victor 2026-05-14 · submenú).
function defaultActionConfigFor(subtype) {
  switch (subtype) {
    case 'add_label':       return { action_type: 'add_label', labels: [] };
    case 'remove_label':    return { action_type: 'remove_label', labels: [] };
    case 'assign_agent':    return { action_type: 'assign_agent', agent_id: null };
    case 'assign_team':     return { action_type: 'assign_team', team_id: null };
    case 'change_stage':    return { action_type: 'change_stage', target_stage_id: null, target_pipeline_id: null };
    case 'change_priority': return { action_type: 'change_priority', priority: 'medium' };
    case 'resolve':         return { action_type: 'resolve' };
    case 'snooze':          return { action_type: 'snooze', snooze_until: null };
    default:                return { action_type: subtype };
  }
}

function defaultConfigFor(type) {
  switch (type) {
    case 'message':        return { content: '' };
    case 'pause':          return { conditions: [{ type: 'until_message' }] };
    case 'condition':      return { field: '', operator: 'equals', value: '' };
    case 'action':         return { action_type: 'add_label', labels: '' };
    case 'reaction':       return { emoji: '❤' };
    case 'comment':        return { content: '' };
    case 'list_message':   return { header: '', body: '', footer: '', sections: [] };
    case 'round_robin':    return { team_id: null };
    case 'validation':     return { expect: 'any', timeout_minutes: 60 };
    case 'subscribe_meta': return { list: 'broadcasts' };
    case 'widget':         return { widget_type: 'calendar', intro: '', url: '' };
    case 'code':           return { code: "# set('clave', 'valor')\n" };
    case 'subbot':         return { bot_id: null };
    default: return {};
  }
}

// Todos los step types se editan inline en la tarjeta — el panel lateral
// queda solo para casos avanzados (list_message JSON sections).
const PANEL_ONLY_TYPES = []; // por ahora ninguno, todos son inline
function onStepSelect(step) {
  if (PANEL_ONLY_TYPES.includes(step.step_type)) {
    selectedStep.value = step;
  } else {
    selectedStep.value = null;
  }
}

async function saveStepConfig({ step, config }) {
  saving.value = true;
  try {
    const { data } = await BotsAPI.updateStep(botId.value, step.id, { config });
    Object.assign(step, data);
    selectedStep.value = null;
  } finally {
    saving.value = false;
  }
}

// Para que el StepNode pueda actualizar config (canales etc.) sin abrir el panel lateral
async function onStepConfigInline({ step, patch }) {
  saving.value = true;
  try {
    const newConfig = { ...(step.config || {}), ...patch };
    const { data } = await BotsAPI.updateStep(botId.value, step.id, { config: newConfig });
    Object.assign(step, data);
  } finally {
    saving.value = false;
  }
}

async function deleteStep(step) {
  // Borrado instantáneo (sin confirm). Cascada nullifica hijos + bot_runs en backend.
  try {
    await BotsAPI.deleteStep(botId.value, step.id);
    const { data } = await BotsAPI.steps(botId.value);
    steps.value = data.payload || [];
    if (selectedStep.value?.id === step.id) selectedStep.value = null;
  } catch (e) {
    // eslint-disable-next-line no-alert
    alert('No se pudo borrar el paso: ' + (e.response?.data?.message || e.message));
  }
}

// Renombrar: guarda label custom en config.label
async function renameStep({ step, label }) {
  const newConfig = { ...(step.config || {}) };
  if (label) newConfig.label = label;
  else delete newConfig.label;
  try {
    const { data } = await BotsAPI.updateStep(botId.value, step.id, { config: newConfig });
    Object.assign(step, data);
  } catch (e) {
    // eslint-disable-next-line no-alert
    alert('Error renombrando: ' + (e.response?.data?.message || e.message));
  }
}

// Duplicar: crea step nuevo con misma config, posición a la derecha con gap, sin parent (flotante)
async function duplicateStep(step) {
  try {
    const { data } = await BotsAPI.createStep(botId.value, {
      step_type: step.step_type,
      parent_step_id: null,
      branch_key: null,
      canvas_x: snap(step.canvas_x + stepWidth(step) + 80),
      canvas_y: snap(step.canvas_y + 60),
      config: JSON.parse(JSON.stringify(step.config || {})),
    });
    steps.value.push(data);
    // Marcar como flotante UI-side para que no se conecte automáticamente al entry
    const next = new Set(disconnectedStepIds.value);
    next.add(data.id);
    disconnectedStepIds.value = next;
    saveLayout();
  } catch (e) {
    // eslint-disable-next-line no-alert
    alert('Error duplicando: ' + (e.response?.data?.message || e.message));
  }
}

// Doble click en una línea conectora → elimina la línea visualmente.
// El step queda flotante (sin entrada). Si tenía parent en backend, se limpia también.
async function disconnectStepFromParent(stepId) {
  const step = steps.value.find(s => s.id === stepId);
  if (!step) return;
  // Si tiene parent en backend, quitarlo
  if (step.parent_step_id) {
    try {
      await BotsAPI.updateStep(botId.value, stepId, { parent_step_id: null, branch_key: null });
      step.parent_step_id = null;
      step.branch_key = null;
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert('Error desconectando: ' + (e.response?.data?.message || e.message));
      return;
    }
  }
  // Marcar como desconectado a nivel UI → connectors computed no dibujará su línea
  const next = new Set(disconnectedStepIds.value);
  next.add(stepId);
  disconnectedStepIds.value = next;
  saveLayout();
}

// ── Modo conexión: lógica completa ──
const ENTRY_SENTINEL = { id: '__entry__' };

function startConnectionMode(fromStep, branchKey = null) {
  palettePos.value = null; // cerrar palette
  // Si fromStep es null → conectamos desde el pill "Iniciar Salesbot" (origen virtual).
  connectingFrom.value = fromStep || ENTRY_SENTINEL;
  // Preserva el branch_key del botón origen (+ rama, + Ninguna coincide, etc.)
  // Para steps non-condition o el "+" genérico, será null y usaremos 'next' al confirmar.
  connectingBranchKey.value = branchKey;
  hoveredTargetId.value = null;
}

function cancelConnection() {
  connectingFrom.value = null;
  connectingBranchKey.value = null;
  hoveredTargetId.value = null;
}

// Walk up the parent chain del source: si llegamos al target, sería ciclo.
function canConnectTo(target) {
  const source = connectingFrom.value;
  if (!source || !target || source.id === target.id) return false;
  // Conexión desde el pill de entrada: solo permitido a steps actualmente desconectados/huérfanos.
  if (source.id === '__entry__') {
    return disconnectedStepIds.value.has(target.id) || !target.parent_step_id;
  }
  let current = source;
  while (current?.parent_step_id) {
    if (current.parent_step_id === target.id) return false;
    current = stepsById.value[current.parent_step_id];
  }
  return true;
}

function onConnectionMouseMove(ev) {
  if (!connectingFrom.value) return;
  const vp = viewportRef.value;
  if (!vp) return;
  const rect = vp.getBoundingClientRect();
  const localX = ev.clientX - rect.left;
  const localY = ev.clientY - rect.top;
  // Convertir a coords del canvas (compensar pan + zoom)
  connectingMousePos.value = {
    x: (localX - pan.value.x) / zoom.value,
    y: (localY - pan.value.y) / zoom.value,
  };
  // Detectar step bajo el cursor
  const el = document.elementFromPoint(ev.clientX, ev.clientY);
  const stepEl = el?.closest?.('[data-step-id]');
  hoveredTargetId.value = stepEl ? +stepEl.dataset.stepId : null;
}

function onConnectionMouseDown(ev) {
  if (!connectingFrom.value) return;
  const stepEl = ev.target?.closest?.('[data-step-id]');
  if (stepEl) {
    const id = +stepEl.dataset.stepId;
    const target = stepsById.value[id];
    if (target && canConnectTo(target)) {
      ev.preventDefault();
      ev.stopPropagation();
      confirmConnection(connectingFrom.value, target);
      return;
    }
  }
  // Click fuera de un step válido → cancelar
  ev.preventDefault();
  ev.stopPropagation();
  cancelConnection();
}

function onConnectionKeyDown(ev) {
  if (ev.key === 'Escape' && connectingFrom.value) {
    ev.preventDefault();
    cancelConnection();
  }
}

async function confirmConnection(source, target) {
  // Si el origen es el pill "Iniciar Salesbot" → target pasa a ser raíz (parent=null).
  const fromEntry = source?.id === '__entry__';
  // Si veníamos de un botón específico (+ rama, + Ninguna coincide), respetamos ese branch_key.
  // Si no, usamos 'next' como default (válido para steps no-condition).
  const finalBranchKey = fromEntry ? null : (connectingBranchKey.value || 'next');
  try {
    // Convergencia multi-entrada: si el target YA tiene padre (otro flujo lo
    // conecta) NO le robamos el conector. Creamos un paso "Ir a → target"
    // hijo del source. Así el conector anterior se conserva y varios flujos
    // pueden apuntar al mismo destino.
    if (!fromEntry && target.parent_step_id && target.parent_step_id !== source.id) {
      const { data } = await BotsAPI.createStep(botId.value, {
        step_type: 'goto',
        parent_step_id: source.id,
        branch_key: finalBranchKey,
        config: { target_step_id: target.id },
      });
      steps.value.push(data);
      connectionFlash.value = { stepId: target.id };
      setTimeout(() => {
        if (connectionFlash.value?.stepId === target.id) connectionFlash.value = null;
      }, 1500);
      return;
    }
    await BotsAPI.updateStep(botId.value, target.id, {
      parent_step_id: fromEntry ? null : source.id,
      branch_key: finalBranchKey,
    });
    target.parent_step_id = fromEntry ? null : source.id;
    target.branch_key = finalBranchKey;
    // Si estaba en el set de desconectados, sacarlo
    if (disconnectedStepIds.value.has(target.id)) {
      const next = new Set(disconnectedStepIds.value);
      next.delete(target.id);
      disconnectedStepIds.value = next;
      saveLayout();
    }
    // Flash de confirmación
    connectionFlash.value = { stepId: target.id };
    setTimeout(() => {
      if (connectionFlash.value?.stepId === target.id) connectionFlash.value = null;
    }, 1500);
  } catch (e) {
    // eslint-disable-next-line no-alert
    alert('Error conectando: ' + (e.response?.data?.message || e.message));
  } finally {
    cancelConnection();
  }
}

// Path SVG fantasma desde el step origen hacia el cursor o el target hover
const ghostLinePath = computed(() => {
  const src = connectingFrom.value;
  if (!src) return '';
  // Origen: botón X del step source, o lado derecho del pill de entrada si origen es entry.
  const from = src.id === '__entry__'
    ? { x: entryNodePos.value.x + 176, y: entryNodePos.value.y + 16 }
    : { x: src.canvas_x + stepWidth(src), y: src.canvas_y + 14 };
  let to;
  const hovered = hoveredTargetId.value && hoveredTargetId.value !== src.id
    ? stepsById.value[hoveredTargetId.value] : null;
  if (hovered) {
    to = { x: hovered.canvas_x, y: hovered.canvas_y + 14 };
  } else {
    to = connectingMousePos.value;
  }
  const dx = Math.max(40, (to.x - from.x) * 0.5);
  return `M${from.x},${from.y} C${from.x + dx},${from.y} ${to.x - dx},${to.y} ${to.x},${to.y}`;
});

// Disponible para conectar:
// - Desde palette de un step: hay al menos 1 step distinto.
// - Desde palette de entry: hay al menos 1 step desconectado para reconectar.
const canConnectFromPalette = computed(() => {
  const parent = palettePos.value?.parentStep;
  if (parent) return steps.value.some(s => s.id !== parent.id);
  return steps.value.some(s => disconnectedStepIds.value.has(s.id));
});

// Activar/desactivar listeners globales según connectingFrom
watch(connectingFrom, (val) => {
  if (val) {
    document.addEventListener('mousemove', onConnectionMouseMove);
    document.addEventListener('mousedown', onConnectionMouseDown, true); // capture
    document.addEventListener('keydown', onConnectionKeyDown);
  } else {
    document.removeEventListener('mousemove', onConnectionMouseMove);
    document.removeEventListener('mousedown', onConnectionMouseDown, true);
    document.removeEventListener('keydown', onConnectionKeyDown);
  }
});

let savePosTimer = null;
function saveStepPosition(step) {
  if (!step) return;
  clearTimeout(savePosTimer);
  savePosTimer = setTimeout(async () => {
    await BotsAPI.updateStep(botId.value, step.id, { canvas_x: step.canvas_x, canvas_y: step.canvas_y });
  }, 300);
}

// ── Drag (con snap a grilla) ────────────────────────────────
function startDrag({ step, ev }) {
  dragging.value = {
    step,
    startX: ev.clientX,
    startY: ev.clientY,
    originX: step.canvas_x,
    originY: step.canvas_y,
  };
  document.addEventListener('mousemove', onDragMove);
  document.addEventListener('mouseup', onDragEnd, { once: true });
}
function onDragMove(ev) {
  if (!dragging.value) return;
  const d = dragging.value;
  // dividir por zoom para que el drag se sienta natural en cualquier nivel
  d.step.canvas_x = Math.max(0, snap(d.originX + (ev.clientX - d.startX) / zoom.value));
  d.step.canvas_y = Math.max(0, snap(d.originY + (ev.clientY - d.startY) / zoom.value));
}
function onDragEnd() {
  if (dragging.value) saveStepPosition(dragging.value.step);
  dragging.value = null;
  document.removeEventListener('mousemove', onDragMove);
}
function snap(v) { return Math.round(v / SNAP) * SNAP; }

// ── Drag de tarjetas decorativas (entry pill, disparadores) ──
function startDecoDrag(key, ev) {
  if (ev.button !== 0) return; // solo left-click
  ev.preventDefault();
  ev.stopPropagation();
  const posRef = key === 'entry' ? entryNodePos : triggerCardPos;
  draggingDeco.value = {
    key,
    startX: ev.clientX,
    startY: ev.clientY,
    originX: posRef.value.x,
    originY: posRef.value.y,
  };
  document.addEventListener('mousemove', onDecoDragMove);
  document.addEventListener('mouseup', onDecoDragEnd, { once: true });
}
function onDecoDragMove(ev) {
  if (!draggingDeco.value) return;
  const d = draggingDeco.value;
  const posRef = d.key === 'entry' ? entryNodePos : triggerCardPos;
  posRef.value = {
    x: Math.max(0, snap(d.originX + (ev.clientX - d.startX) / zoom.value)),
    y: Math.max(0, snap(d.originY + (ev.clientY - d.startY) / zoom.value)),
  };
}
function onDecoDragEnd() {
  draggingDeco.value = null;
  document.removeEventListener('mousemove', onDecoDragMove);
  saveLayout();
}

// ── Persistencia layout en localStorage por bot ──
function layoutKey() { return `salesbot-${botId.value}-layout`; }
function loadLayout() {
  try {
    const raw = localStorage.getItem(layoutKey());
    if (!raw) return;
    const data = JSON.parse(raw);
    if (data.entry) entryNodePos.value = data.entry;
    if (data.trigger) triggerCardPos.value = data.trigger;
    if (Array.isArray(data.disconnected)) {
      disconnectedStepIds.value = new Set(data.disconnected);
    }
  } catch (e) { /* ignore */ }
}
function saveLayout() {
  try {
    localStorage.setItem(layoutKey(), JSON.stringify({
      entry: entryNodePos.value,
      trigger: triggerCardPos.value,
      disconnected: Array.from(disconnectedStepIds.value),
    }));
  } catch (e) { /* ignore */ }
}

// ── Pan/Zoom ────────────────────────────────────────────────
const viewportRef = ref(null);

function onCanvasMouseDown(ev) {
  // Middle-click siempre panea (compatible con apps de diseño)
  // Left-click panea SI el target es el canvas vacío (no un nodo)
  const isMiddle = ev.button === 1;
  const isLeftOnEmpty = ev.button === 0 && ev.target.matches?.('[data-canvas]');
  if (!isMiddle && !isLeftOnEmpty) return;
  ev.preventDefault();
  panning.value = { startX: ev.clientX, startY: ev.clientY, origX: pan.value.x, origY: pan.value.y };
  document.addEventListener('mousemove', onPanMove);
  document.addEventListener('mouseup', onPanEnd, { once: true });
}
function onPanMove(ev) {
  if (!panning.value) return;
  const p = panning.value;
  pan.value = { x: p.origX + (ev.clientX - p.startX), y: p.origY + (ev.clientY - p.startY) };
  clampPan();
}
function onPanEnd() {
  panning.value = null;
  document.removeEventListener('mousemove', onPanMove);
}

// Zoom inteligente: aplica zoom manteniendo el punto bajo el pivot fijo.
// Si pivotX/Y son null → zoom al centro del viewport (botones del toolbar).
// Si vienen del wheel → zoom hacia el cursor (estilo Figma/Miro).
function applyZoom(targetZoom, pivotX = null, pivotY = null) {
  const newZoom = Math.max(0.3, Math.min(2, +targetZoom.toFixed(2)));
  if (newZoom === zoom.value) return;
  const vp = viewportRef.value;
  if (!vp) { zoom.value = newZoom; return; }
  const rect = vp.getBoundingClientRect();
  const px = pivotX !== null ? pivotX - rect.left : rect.width / 2;
  const py = pivotY !== null ? pivotY - rect.top : rect.height / 2;
  // Punto del canvas que está bajo el pivot ANTES del zoom
  const canvasX = (px - pan.value.x) / zoom.value;
  const canvasY = (py - pan.value.y) / zoom.value;
  zoom.value = newZoom;
  // Ajustar pan para que ese mismo punto siga bajo el pivot DESPUÉS
  pan.value = {
    x: px - canvasX * newZoom,
    y: py - canvasY * newZoom,
  };
  clampPan();
}

function zoomIn()  { applyZoom(zoom.value + 0.1); }
function zoomOut() { applyZoom(zoom.value - 0.1); }
function zoomReset() { zoom.value = 1; pan.value = { x: 0, y: 0 }; }

function fitToScreen() {
  const all = steps.value.map(s => ({ x: s.canvas_x, y: s.canvas_y }));
  all.push({ x: entryNodePos.value.x, y: entryNodePos.value.y });
  if (!all.length) return;
  const minX = Math.min(...all.map(p => p.x)) - 60;
  const minY = Math.min(...all.map(p => p.y)) - 60;
  const maxX = Math.max(...all.map(p => p.x + STEP_W)) + 60;
  const maxY = Math.max(...all.map(p => p.y + 100)) + 60;
  const vp = viewportRef.value;
  if (!vp) return;
  const w = vp.clientWidth, h = vp.clientHeight;
  const z = Math.min(w / (maxX - minX), h / (maxY - minY), 1);
  zoom.value = Math.max(0.3, +z.toFixed(2));
  pan.value = { x: -minX * zoom.value + 30, y: -minY * zoom.value + 30 };
}

function onWheel(ev) {
  ev.preventDefault();

  // Zoom: Ctrl/Cmd + wheel · pinch del trackpad (que también setea ctrlKey)
  if (ev.ctrlKey || ev.metaKey) {
    // Trackpad pinch: deltaY pequeño y continuo. Mouse wheel: pasos discretos de ±100 aprox.
    // Usamos una unidad de zoom proporcional al delta (más suave en trackpad)
    const zoomStep = Math.abs(ev.deltaY) > 50 ? 0.1 : 0.05;
    applyZoom(zoom.value + (ev.deltaY < 0 ? zoomStep : -zoomStep), ev.clientX, ev.clientY);
    return;
  }

  // Pan: scroll normal (vertical) · trackpad two-finger · Shift+wheel (horizontal)
  let dx = ev.deltaX;
  let dy = ev.deltaY;
  // Shift+wheel sin deltaX → convertir a horizontal (atajo común con mouse de scroll vertical)
  if (ev.shiftKey && dx === 0) {
    dx = dy;
    dy = 0;
  }
  pan.value = {
    x: pan.value.x - dx,
    y: pan.value.y - dy,
  };
  clampPan();
}

// ── Minimap interactivo ─────────────────────────────────────
const MINIMAP_W = 180;
const MINIMAP_H = 110;
const minimapScale = computed(() => MINIMAP_W / CANVAS_W);

function onMinimapClick(ev) {
  const rect = ev.currentTarget.getBoundingClientRect();
  const localX = ev.clientX - rect.left;
  const localY = ev.clientY - rect.top;
  // navegar el viewport para centrarse en este punto del canvas
  const targetX = localX / minimapScale.value;
  const targetY = localY / (MINIMAP_H / CANVAS_H);
  const vp = viewportRef.value;
  if (!vp) return;
  pan.value = {
    x: -targetX * zoom.value + vp.clientWidth / 2,
    y: -targetY * zoom.value + vp.clientHeight / 2,
  };
}

// rectángulo del viewport en el minimap
const viewportRect = computed(() => {
  const vp = viewportRef.value;
  if (!vp) return { x: 0, y: 0, w: 0, h: 0 };
  return {
    x: -pan.value.x / zoom.value * minimapScale.value,
    y: -pan.value.y / zoom.value * (MINIMAP_H / CANVAS_H),
    w: vp.clientWidth / zoom.value * minimapScale.value,
    h: vp.clientHeight / zoom.value * (MINIMAP_H / CANVAS_H),
  };
});

// ── Triggers ────────────────────────────────────────────────
function openNewTrigger() {
  selectedTrigger.value = { trigger_type: 'message_received', config: {}, active: true };
  showTriggerEditor.value = true;
}
function openTrigger(t) {
  selectedTrigger.value = t;
  showTriggerEditor.value = true;
}
async function saveTrigger({ trigger, payload }) {
  if (trigger.id) {
    const { data } = await BotsAPI.updateTrigger(botId.value, trigger.id, payload);
    const idx = triggers.value.findIndex(x => x.id === trigger.id);
    if (idx >= 0) triggers.value[idx] = data;
  } else {
    const { data } = await BotsAPI.createTrigger(botId.value, payload);
    triggers.value.push(data);
  }
  showTriggerEditor.value = false;
  const { data: bdata } = await BotsAPI.show(botId.value);
  bot.value = bdata;
}
async function deleteTrigger(trigger) {
  // Borrado instantáneo · sin confirm — Victor pidió que cerrar/eliminar
  // siempre sea click directo en cualquier parte del editor.
  await BotsAPI.deleteTrigger(botId.value, trigger.id);
  triggers.value = triggers.value.filter(t => t.id !== trigger.id);
  showTriggerEditor.value = false;
}

// ── Bot name edit ───────────────────────────────────────────
const editingName = ref(false);
const nameDraft = ref('');
function startEditName() { editingName.value = true; nameDraft.value = bot.value.name; nextTick(() => document.getElementById('bot-name-input')?.focus()); }
async function saveBotName() {
  if (nameDraft.value && nameDraft.value !== bot.value.name) {
    const { data } = await BotsAPI.update(bot.value.id, { name: nameDraft.value });
    bot.value = data;
  }
  editingName.value = false;
}

// ── Bot switcher (pill ▾ → dropdown con todos los bots) ───────
const showBotSwitcher = ref(false);
const allBots = ref([]);
const botSearchQuery = ref('');

const filteredBots = computed(() => {
  const q = botSearchQuery.value.trim().toLowerCase();
  if (!q) return allBots.value;
  return allBots.value.filter(b => (b.name || '').toLowerCase().includes(q));
});

async function toggleBotSwitcher() {
  if (showBotSwitcher.value) {
    showBotSwitcher.value = false;
    return;
  }
  // Cargar lista solo la primera vez (refrescamos siempre por si hay nuevos bots)
  try {
    const { data } = await BotsAPI.get();
    allBots.value = data.payload || [];
  } catch (_) { /* silencioso */ }
  botSearchQuery.value = '';
  showBotSwitcher.value = true;
}
function closeBotSwitcher() { showBotSwitcher.value = false; }
function selectBot(b) {
  closeBotSwitcher();
  if (b.id === bot.value?.id) return;
  router.push({ name: 'salesbots_editor', params: { accountId: route.params.accountId, botId: b.id } });
}

// ── Settings menu (engranaje) ───────────────────────────────
const showSettingsMenu = ref(false);
const themeMode = ref(LocalStorage.get(LOCAL_STORAGE_KEYS.COLOR_SCHEME) || 'auto');
const themeIcon = computed(() => ({
  light: 'i-lucide-sun',
  dark:  'i-lucide-moon',
  auto:  'i-lucide-monitor-cog',
}[themeMode.value]));

function settingsRename() {
  showSettingsMenu.value = false;
  startEditName();
}

function settingsCycleTheme() {
  const order = ['light', 'dark', 'auto'];
  const idx = order.indexOf(themeMode.value);
  const next = order[(idx + 1) % order.length];
  themeMode.value = next;
  LocalStorage.set(LOCAL_STORAGE_KEYS.COLOR_SCHEME, next);
  const isOSOnDarkMode = window.matchMedia('(prefers-color-scheme: dark)').matches;
  setColorTheme(isOSOnDarkMode);
  // no cierro el menú — el usuario suele querer cyclar varias veces para ver
}

function toggleSettingsMenu() { showSettingsMenu.value = !showSettingsMenu.value; }
function closeSettingsMenu() { showSettingsMenu.value = false; }

// Cierra el menú pero deja al usuario en el editor. Los cambios ya están
// autoguardados step a step; mostramos confirmación visible (Victor 2026-05-26).
function settingsSaveAndContinue() {
  showSettingsMenu.value = false;
  useAlert('Guardado ✓ · puedes seguir editando');
}

// Confirma y sale del editor a la lista de bots. Cambios ya autoguardados.
function settingsExitAndSave() {
  showSettingsMenu.value = false;
  back();
}

// Sale del editor sin esperar más cambios. Como el autosave es por step, lo
// que ya tecleó el usuario está guardado · este botón solo materializa la
// intención de "salir" sin extra confirm.
function settingsExitWithoutSaving() {
  showSettingsMenu.value = false;
  back();
}

function back() {
  router.push({ name: 'salesbots_index', params: { accountId: route.params.accountId } });
}

// ── Auto-recenter: si estás perdido (sin steps visibles) por 10s, te lleva al inicio ──
const LOST_THRESHOLD_S = 10;
let lostSecs = 0;
let lostTimer = null;

function isLostInEmptyArea() {
  const vp = viewportRef.value;
  if (!vp) return false;
  const z = zoom.value;
  const W = vp.clientWidth;
  const H = vp.clientHeight;
  // ¿Está visible el entry pill? (ancho ~176, alto ~32)
  const entryScreenX = entryNodePos.value.x * z + pan.value.x;
  const entryScreenY = entryNodePos.value.y * z + pan.value.y;
  const entryVisible = entryScreenX > -176 && entryScreenX < W && entryScreenY > -32 && entryScreenY < H;
  if (entryVisible) return false;
  // ¿Hay algún step visible? (asumimos card de ~300x100)
  const anyStepVisible = steps.value.some(s => {
    const x = s.canvas_x * z + pan.value.x;
    const y = s.canvas_y * z + pan.value.y;
    return x > -300 && x < W && y > -100 && y < H;
  });
  return !anyStepVisible;
}

function recenterToEntry() {
  const vp = viewportRef.value;
  if (!vp) return;
  // Posicionar entry pill cerca de la esquina superior izquierda (con el rejuego de margen).
  pan.value = {
    x: PAN_LEFT_MARGIN - entryNodePos.value.x * zoom.value,
    y: PAN_TOP_MARGIN - entryNodePos.value.y * zoom.value,
  };
  clampPan();
  lostSecs = 0;
}

onMounted(() => {
  load();
  lostTimer = setInterval(() => {
    if (isLostInEmptyArea()) {
      lostSecs += 1;
      if (lostSecs >= LOST_THRESHOLD_S) recenterToEntry();
    } else {
      lostSecs = 0;
    }
  }, 1000);
});

onBeforeUnmount(() => {
  if (lostTimer) clearInterval(lostTimer);
  document.removeEventListener('mousemove', onDragMove);
  document.removeEventListener('mousemove', onPanMove);
  document.removeEventListener('mousemove', onDecoDragMove);
  document.removeEventListener('mousemove', onConnectionMouseMove);
  document.removeEventListener('mousedown', onConnectionMouseDown, true);
  document.removeEventListener('keydown', onConnectionKeyDown);
});
</script>

<template>
  <div class="w-full bg-n-background" style="height: calc(100vh - 60px); display: flex; flex-direction: column;">
    <!-- Toolbar superior -->
    <div class="border-b border-n-weak px-5 py-2.5 flex items-center justify-between bg-n-background">
      <!-- Izquierda: sólo back -->
      <button class="text-n-slate-11 hover:text-n-slate-12 text-xl leading-none" @click="back">←</button>

      <!-- Derecha: pill (icono · nombre cliqueable=editar · ▾ cliqueable=switcher) + engranaje -->
      <div class="flex items-center gap-2.5">
        <span v-if="saving" class="text-[11px] text-n-slate-11 mr-1">guardando…</span>

        <!-- Pill compuesta: 3 zonas (icono / nombre / arrow) -->
        <div v-on-click-outside="closeBotSwitcher" class="relative">
          <div class="flex items-stretch rounded-lg overflow-hidden border-[1.5px] transition-all"
               :class="showBotSwitcher ? '' : ''"
               :style="{
                 background: 'linear-gradient(135deg, #ecfdf5, #ffffff)',
                 borderColor: '#5eead4'
               }">
            <!-- Zona 1: icono robot (decorativo) -->
            <div class="flex items-center justify-center w-9 border-r"
                 style="background: rgba(20,184,166,.08); border-right-color: rgba(94,234,212,.4);">
              <div class="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs"
                   style="background: linear-gradient(135deg, #14b8a6, #0d9488);">🤖</div>
            </div>

            <!-- Zona 2: nombre cliqueable → entra en modo edición -->
            <div v-if="!editingName"
                 class="px-3 py-2 cursor-text flex items-center transition-colors"
                 style="min-width: 140px;"
                 title="Click para renombrar"
                 @click.stop="startEditName"
                 @mouseover="$event.currentTarget.style.background = 'rgba(20,184,166,.08)'"
                 @mouseleave="$event.currentTarget.style.background = ''">
              <span class="text-[13px] font-bold tracking-wide" style="color: #0d9488;">{{ bot ? bot.name : '…' }}</span>
            </div>
            <div v-else class="px-2 py-1.5 flex items-center" style="background: rgba(245,158,11,.1);">
              <input id="bot-name-input" v-model="nameDraft"
                     class="bg-transparent outline-none text-[13px] font-bold tracking-wide rounded px-1.5 py-0.5"
                     style="color: #92400e; border: 1px solid #f59e0b; min-width: 200px;"
                     @blur="saveBotName" @keyup.enter="saveBotName" @keyup.esc="editingName = false" />
            </div>

            <!-- Zona 3: arrow ▾ → toggle dropdown switcher -->
            <button type="button"
                    class="w-8 flex items-center justify-center cursor-pointer transition-all border-l"
                    :style="{
                      borderLeftColor: 'rgba(94,234,212,.4)',
                      color: '#14b8a6',
                      background: showBotSwitcher ? 'rgba(20,184,166,.15)' : 'transparent',
                    }"
                    title="Cambiar de bot"
                    @click.stop="toggleBotSwitcher">
              <span class="text-[11px] inline-block transition-transform"
                    :style="{ transform: showBotSwitcher ? 'rotate(180deg)' : 'rotate(0)' }">▾</span>
            </button>
          </div>

          <!-- Dropdown con la lista de bots -->
          <div v-if="showBotSwitcher"
               class="absolute right-0 mt-1.5 min-w-[280px] max-h-[400px] overflow-y-auto bg-n-background border border-n-weak rounded-lg shadow-xl p-1.5 z-50">
            <div class="px-2.5 py-1.5 mb-1 rounded-md flex items-center gap-1.5 bg-n-alpha-black1">
              <span class="text-n-slate-11 text-[11px]">🔍</span>
              <input v-model="botSearchQuery" type="text" placeholder="Buscar bot…"
                     class="bg-transparent outline-none text-[12px] text-n-slate-12 flex-1"
                     @click.stop />
            </div>
            <div v-for="b in filteredBots" :key="b.id"
                 class="flex items-center gap-2 px-2.5 py-1.5 rounded-md cursor-pointer transition-colors"
                 :class="b.id === bot?.id
                   ? 'font-bold'
                   : 'text-n-slate-12 hover:bg-n-alpha-black1'"
                 :style="b.id === bot?.id ? { background: '#ecfdf5', color: '#0d9488' } : ''"
                 @click="selectBot(b)">
              <span class="w-1.5 h-1.5 rounded-full flex-shrink-0"
                    :style="{ background: b.active ? '#10b981' : '#cbd5e1' }"></span>
              <span class="text-[12px] flex-1 truncate">{{ b.name }}</span>
              <span v-if="b.id === bot?.id" class="text-[11px]" style="color: #0d9488;">✓</span>
            </div>
            <div v-if="filteredBots.length === 0" class="px-2.5 py-3 text-[11px] text-n-slate-11 italic text-center">
              Sin resultados
            </div>
          </div>
        </div>

        <!-- ⚙ Menú de ajustes del bot · repuesto y alineado con la pill del
             nombre (Victor 2026-05-26). Cambios autoguardan; "Guardar y
             continuar" da confirmación visible. -->
        <div v-on-click-outside="closeSettingsMenu" class="relative">
          <button
            type="button"
            class="w-9 h-9 rounded-lg flex items-center justify-center cursor-pointer transition-all"
            :style="{
              background: showSettingsMenu ? 'rgba(13,148,136,.22)' : 'rgba(13,148,136,.12)',
              border: '1px solid rgba(13,148,136,.4)',
              color: '#2dd4bf',
            }"
            title="Opciones del bot"
            @click.stop="toggleSettingsMenu"
          >
            <span class="i-lucide-settings w-[18px] h-[18px]" />
          </button>

          <div
            v-if="showSettingsMenu"
            class="absolute right-0 mt-1.5 w-60 bg-n-background border border-n-weak rounded-xl shadow-xl p-1.5 z-50"
          >
            <div class="px-3 py-2 text-[10.5px] text-n-slate-11">💾 Tus cambios se guardan solos al editar</div>
            <button type="button"
              class="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-[13.5px] font-semibold text-left text-teal-300"
              style="background: linear-gradient(135deg, rgba(13,148,136,.18), rgba(13,148,136,.05));"
              @click="settingsSaveAndContinue">
              <span class="i-lucide-check w-4 h-4" /> Guardar y continuar
            </button>
            <button type="button"
              class="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-[13.5px] text-left text-n-slate-12 hover:bg-n-alpha-black1"
              @click="settingsExitAndSave">
              <span class="i-lucide-door-open w-4 h-4" /> Guardar y salir
            </button>
            <div class="h-px bg-n-weak mx-2 my-1.5" />
            <button type="button"
              class="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-[13.5px] text-left text-n-slate-12 hover:bg-n-alpha-black1"
              @click="settingsRename">
              <span class="i-lucide-pencil w-4 h-4" /> Cambiar nombre del bot
            </button>
            <div class="h-px bg-n-weak mx-2 my-1.5" />
            <button type="button"
              class="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-[13.5px] text-left text-red-500 hover:bg-red-900/20"
              @click="settingsExitWithoutSaving">
              <span class="i-lucide-x w-4 h-4" /> Salir sin guardar
            </button>
          </div>
        </div>

      </div>
    </div>

    <div v-if="loading" class="flex-1 flex items-center justify-center text-sm text-n-slate-11">Cargando bot…</div>

    <div v-else class="flex-1 flex min-h-0">
      <!-- Viewport con zoom/pan -->
      <div
        ref="viewportRef"
        data-canvas
        class="flex-1 relative overflow-hidden bg-n-background select-none"
        :class="[connectingFrom ? '!cursor-crosshair' : (panning ? 'cursor-grabbing' : 'cursor-grab')]"
        @mousedown="onCanvasMouseDown"
        @wheel="onWheel"
      >
        <!-- Toolbar flotante de zoom -->
        <div class="absolute top-3 right-3 z-20 flex items-center gap-1 bg-n-background border border-n-weak rounded-lg px-2 py-1 shadow-sm text-xs">
          <button class="px-2 py-1 hover:bg-n-alpha-black2 rounded" title="Acercar" @click="zoomIn">+</button>
          <span class="text-n-slate-11 px-1 min-w-[40px] text-center">{{ Math.round(zoom * 100) }}%</span>
          <button class="px-2 py-1 hover:bg-n-alpha-black2 rounded" title="Alejar" @click="zoomOut">−</button>
          <span class="border-l border-n-weak h-4 mx-1" />
          <button class="px-2 py-1 hover:bg-n-alpha-black2 rounded" title="Ajustar a pantalla" @click="fitToScreen">⊡</button>
          <button class="px-2 py-1 hover:bg-n-alpha-black2 rounded" title="100% &amp; centrar" @click="zoomReset">1:1</button>
          <span class="border-l border-n-weak h-4 mx-1" />
          <button class="px-2 py-1 hover:bg-n-alpha-black2 rounded text-blue-600" title="Auto-organizar" @click="applyAutoLayout">⤢ Auto</button>
        </div>

        <!-- Hint de pan -->
        <div class="absolute bottom-3 left-3 z-20 px-2.5 py-1 bg-slate-900/80 text-white text-[10px] rounded">
          Scroll para mover · Shift+scroll horizontal · Cmd/Ctrl+scroll para zoom · Click+arrastrar también pannea
        </div>

        <!-- Banner: modo conexión activo -->
        <div v-if="connectingFrom"
             class="absolute top-3 left-1/2 -translate-x-1/2 z-30 px-4 py-2 bg-emerald-600 text-white text-xs rounded-lg shadow-lg flex items-center gap-2">
          <span class="i-lucide-link-2 w-3.5 h-3.5"></span>
          <span>Modo conexión · Click en un paso para conectar · <kbd class="bg-white/20 px-1.5 py-0.5 rounded text-[10px] font-mono">esc</kbd> cancelar</span>
        </div>

        <!-- Canvas con transform -->
        <div
          data-canvas
          class="absolute top-0 left-0 origin-top-left"
          :style="{
            width: CANVAS_W + 'px',
            height: CANVAS_H + 'px',
            transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
          }"
        >
          <!-- Trigger card (arrastrable) -->
          <div class="absolute cursor-move"
               :style="{ left: triggerCardPos.x + 'px', top: triggerCardPos.y + 'px' }"
               @mousedown="startDecoDrag('trigger', $event)">
            <div class="bg-n-background border border-n-weak rounded-lg shadow-sm p-3 w-60">
              <div class="text-[10px] uppercase tracking-wider text-n-slate-11 font-semibold">Disparadores</div>
              <p class="text-[11px] text-n-slate-11 mt-1.5 leading-snug">
                Lanza el bot automáticamente según las reglas que establezcas.
              </p>
              <ul class="mt-2 space-y-1">
                <li v-for="t in triggers" :key="t.id" class="flex items-center gap-1.5 group">
                  <span class="px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 text-[10px] flex-1 cursor-pointer hover:bg-amber-200"
                        @mousedown.stop @click.stop="openTrigger(t)">
                    {{ t.human_name }}
                  </span>
                  <button class="text-n-slate-11 opacity-0 group-hover:opacity-100 text-xs"
                          @mousedown.stop @click.stop="deleteTrigger(t)">×</button>
                </li>
              </ul>
              <button class="mt-2 w-full px-2 py-1 text-[11px] border border-dashed border-n-weak rounded text-n-slate-11 hover:border-blue-400 hover:text-blue-600"
                      @mousedown.stop @click.stop="openNewTrigger">
                + Disparador
              </button>
            </div>
          </div>

          <!-- Iniciar Salesbot pill (arrastrable) -->
          <div class="absolute cursor-move"
               :style="{ left: entryNodePos.x + 'px', top: entryNodePos.y + 'px' }"
               @mousedown="startDecoDrag('entry', $event)">
            <div class="w-44 bg-emerald-50 border-2 border-emerald-400 rounded-lg px-3 py-2 inline-flex items-center justify-between gap-2">
              <span class="inline-flex items-center gap-2">
                <span class="text-emerald-600">▶</span>
                <span class="text-xs font-medium text-emerald-700">Iniciar Salesbot</span>
              </span>
              <button v-if="!hasRootStep" class="text-emerald-700 text-xs hover:bg-emerald-100 rounded px-1" title="Agregar paso o conector"
                      @mousedown.stop @click.stop="openPaletteFromEntry">+</button>
            </div>
          </div>

          <!-- Connectors SVG · estilo Kommo: líneas finas, discretas · z-index: 5 para que las líneas queden por delante del card -->
          <svg class="absolute top-0 left-0" style="pointer-events: none; z-index: 5;" :width="CANVAS_W" :height="CANVAS_H">
            <defs>
              <marker id="arrowhead" viewBox="0 0 6 6" markerWidth="5" markerHeight="5" refX="5" refY="3" orient="auto">
                <path d="M 0 1 L 5 3 L 0 5 z" fill="#94a3b8" />
              </marker>
            </defs>
            <!-- Path invisible más ancho para hit detection del doble click -->
            <path v-for="line in connectors" :key="`hit-${line.id}`"
                  :d="pathFor(line)"
                  stroke="transparent" stroke-width="14" fill="none"
                  style="pointer-events: stroke; cursor: pointer;"
                  @dblclick.stop="disconnectStepFromParent(line.id)">
              <title>Doble click para desconectar este conector</title>
            </path>
            <!-- Path visible -->
            <path v-for="line in connectors" :key="line.id"
                  :d="pathFor(line)"
                  stroke="#94a3b8" stroke-width="1.25" fill="none"
                  stroke-linecap="round"
                  marker-end="url(#arrowhead)" />
            <text v-for="line in connectors.filter(l => l.label)"
                  :key="'lbl-' + line.id"
                  :x="(line.from.x + line.to.x) / 2"
                  :y="(line.from.y + line.to.y) / 2 - 6"
                  text-anchor="middle"
                  class="text-[10px]"
                  :fill="line.labelColor || '#475569'">
              {{ line.label }}
            </text>

            <!-- Ghost line durante modo conexión -->
            <defs v-if="connectingFrom">
              <marker id="arrowhead-ghost" viewBox="0 0 8 8" markerWidth="6" markerHeight="6" refX="7" refY="4" orient="auto">
                <path d="M 0 1 L 7 4 L 0 7 z" fill="#10b981" />
              </marker>
            </defs>
            <path v-if="connectingFrom"
                  :d="ghostLinePath"
                  stroke="#10b981" stroke-width="1.5" fill="none"
                  stroke-linecap="round" stroke-dasharray="5 5"
                  marker-end="url(#arrowhead-ghost)">
              <animate attributeName="stroke-dashoffset" from="0" to="-20" dur="0.8s" repeatCount="indefinite"/>
            </path>
          </svg>

          <!-- Steps -->
          <StepNode
            v-for="step in nodeSteps"
            :key="step.id"
            :step="step"
            :selected="selectedStep?.id === step.id"
            :available-inboxes="availableInboxes"
            :available-bots="availableBots"
            :available-pipelines="availablePipelines"
            :children-count="steps.filter(s => s.parent_step_id === step.id).length"
            :connected-branches="steps.filter(s => s.parent_step_id === step.id).map(s => s.branch_key).filter(Boolean)"
            :is-root="!step.parent_step_id && !disconnectedStepIds.has(step.id)"
            :class="{
              'connection-target-active': connectingFrom && hoveredTargetId === step.id && step.id !== connectingFrom.id && canConnectTo(step),
              'connection-target-invalid': connectingFrom && hoveredTargetId === step.id && (step.id === connectingFrom.id || !canConnectTo(step)),
              'connection-source-active': connectingFrom && connectingFrom.id === step.id,
              'connection-flash': connectionFlash && connectionFlash.stepId === step.id,
            }"
            @select="onStepSelect"
            @add-child="(parent, ev) => openPaletteFromNode(parent, ev)"
            @add-branch="({ step, key }, ev) => openPaletteFromNode(step, ev, key)"
            @delete="deleteStep"
            @rename="renameStep"
            @duplicate="duplicateStep"
            @drag-start="startDrag"
            @update-config="onStepConfigInline"
          />

          <div v-if="!steps.length" class="absolute" style="left: 600px; top: 320px;">
            <div class="px-3 py-2 bg-blue-50 border border-blue-200 rounded text-[11px] text-blue-800">
              Click en el <strong>+</strong> del pill verde para agregar el primer paso del flujo.
            </div>
          </div>
        </div>

        <!-- Minimap interactivo -->
        <div
          class="absolute bottom-3 right-3 z-20 bg-n-background border border-n-weak rounded-lg shadow-md cursor-pointer overflow-hidden"
          :style="{ width: MINIMAP_W + 'px', height: MINIMAP_H + 'px' }"
          @click="onMinimapClick"
        >
          <div class="text-[9px] text-n-slate-11 px-1.5 pt-1">Mini-mapa</div>
          <svg :width="MINIMAP_W" :height="MINIMAP_H" class="block">
            <!-- Trigger card -->
            <rect :x="60 * minimapScale" :y="200 * (MINIMAP_H / CANVAS_H)" :width="240 * minimapScale" :height="100 * (MINIMAP_H / CANVAS_H)" fill="#e2e8f0" />
            <!-- Entry pill -->
            <rect :x="entryNodePos.x * minimapScale" :y="entryNodePos.y * (MINIMAP_H / CANVAS_H)" :width="176 * minimapScale" :height="32 * (MINIMAP_H / CANVAS_H)" fill="#10b981" />
            <!-- Steps -->
            <rect v-for="s in nodeSteps" :key="s.id"
                  :x="s.canvas_x * minimapScale" :y="s.canvas_y * (MINIMAP_H / CANVAS_H)"
                  :width="STEP_W * minimapScale" :height="80 * (MINIMAP_H / CANVAS_H)"
                  :fill="selectedStep?.id === s.id ? '#3b82f6' : '#cbd5e1'" />
            <!-- Viewport rect -->
            <rect :x="viewportRect.x" :y="viewportRect.y" :width="viewportRect.w" :height="viewportRect.h"
                  fill="none" stroke="#3b82f6" stroke-width="1.5" stroke-dasharray="3,2" />
          </svg>
        </div>

        <!-- Step palette popover -->
        <StepPalettePopover v-if="palettePos" :position="palettePos"
                            :can-connect="canConnectFromPalette"
                            @pick="pickStepType"
                            @pick-connector="startConnectionMode(palettePos.parentStep, palettePos.branchKey)"
                            @close="palettePos = null" />
      </div>

      <!-- Right panel -->
      <StepConfigPanel
        v-if="selectedStep"
        :step="selectedStep"
        :available-bots="availableBots"
        @save="saveStepConfig"
        @close="selectedStep = null"
      />
    </div>

    <!-- Modal de Disparador (fuera del flow para tener inset-0) -->
    <TriggerEditor
      v-if="showTriggerEditor && selectedTrigger"
      :trigger="selectedTrigger"
      :bot-name="bot?.name || 'SALESBOT'"
      :available-inboxes="availableInboxes"
      :available-labels="availableLabels"
      :available-agents="availableAgents"
      :available-pipelines="availablePipelines"
      @save="saveTrigger"
      @delete="deleteTrigger"
      @close="showTriggerEditor = false"
    />
  </div>
</template>

<style scoped>
/* Modo conexión: feedback visual en steps */
:deep(.connection-target-active) {
  outline: 2px solid #10b981 !important;
  outline-offset: 3px;
  border-radius: 14px;
  box-shadow: 0 0 0 4px rgba(16, 185, 129, 0.18) !important;
  transition: outline 0.12s ease, box-shadow 0.12s ease;
}
:deep(.connection-target-invalid) {
  outline: 2px solid #ef4444 !important;
  outline-offset: 3px;
  border-radius: 14px;
  cursor: not-allowed !important;
}
:deep(.connection-source-active) {
  outline: 2px solid #34d399 !important;
  outline-offset: 3px;
  border-radius: 14px;
  box-shadow: 0 0 0 4px rgba(52, 211, 153, 0.20) !important;
}
:deep(.connection-flash) {
  animation: connection-flash-anim 1.5s ease forwards;
}
@keyframes connection-flash-anim {
  0%   { outline: 3px solid #10b981; outline-offset: 4px; box-shadow: 0 0 0 6px rgba(16,185,129,.35); }
  60%  { outline: 2px solid #10b981; outline-offset: 3px; box-shadow: 0 0 0 4px rgba(16,185,129,.20); }
  100% { outline: 0 solid transparent; outline-offset: 0;   box-shadow: 0 0 0 0 rgba(16,185,129,0); }
}
</style>
